<template>
  <div class="timezone-selector-wrapper">
    <label v-if="label" :for="timezoneSelectorId" class="timezone-selector__label">
      {{ label }}
      <span v-if="required" class="timezone-selector__required">*</span>
    </label>
    <div
      :id="timezoneSelectorId"
      ref="triggerRef"
      class="timezone-selector__trigger"
      :class="{ 'timezone-selector__trigger--open': isOpen }"
      @click="toggleDropdown"
      @blur="handleBlur"
      tabindex="0"
    >
      <Icon icon="mdi:earth" class="timezone-selector__trigger-globe" />
      <span class="timezone-selector__trigger-text">
        {{ selectedTimezoneLabel || "Select Timezone" }}
      </span>
      <span v-if="selectedTimezone" class="timezone-selector__trigger-time">
        {{ selectedTimezoneTime }}
      </span>
      <Icon icon="mdi:chevron-down" class="timezone-selector__trigger-icon" />
    </div>

    <Teleport to="body">
      <div v-if="isOpen" class="timezone-selector" :style="dropdownStyle">
        <div class="timezone-selector__search">
          <Input
            v-model="searchQuery"
            type="text"
            placeholder="Search..."
            leadingIcon="mdi:magnify"
            :clearable="!!searchQuery"
            size="sm"
            @click.stop
            @mousedown.stop
            @focus.stop
          />
        </div>

        <div class="timezone-selector__header">
          <span class="timezone-selector__header-label">TIME ZONE</span>
          <div v-if="showFormatToggle" class="timezone-selector__toggle">
            <span
              class="timezone-selector__toggle-option"
              :class="{ 'timezone-selector__toggle-option--active': !use24Hour }"
            >
              am/pm
            </span>
            <button
              class="timezone-selector__toggle-switch"
              :class="{ 'timezone-selector__toggle-switch--active': use24Hour }"
              @click.stop="use24Hour = !use24Hour"
            >
              <span class="timezone-selector__toggle-slider"></span>
            </button>
            <span
              class="timezone-selector__toggle-option"
              :class="{ 'timezone-selector__toggle-option--active': use24Hour }"
            >
              24h
            </span>
          </div>
        </div>

        <div class="timezone-selector__list">
          <div v-if="filteredTimezoneGroups.length === 0" class="timezone-selector__empty">
            No timezones found for "{{ searchQuery }}"
          </div>
          <template v-else>
            <div
              v-for="(group, groupIndex) in filteredTimezoneGroups"
              :key="groupIndex"
              class="timezone-selector__group"
            >
              <div class="timezone-selector__group-header">
                {{ group.region }}
                <span class="timezone-selector__group-count">{{ group.timezones.length }}</span>
              </div>
              <div
                v-for="timezone in group.timezones"
                :key="timezone.value"
                class="timezone-selector__item"
                :class="{
                  'timezone-selector__item--selected': selectedTimezone === timezone.value,
                }"
                @click.stop="selectTimezone(timezone.value)"
              >
                <div class="timezone-selector__item-main">
                  <span class="timezone-selector__item-name">{{ timezone.label }}</span>
                  <span
                    v-if="timezone.cities?.length && searchQuery"
                    class="timezone-selector__item-cities"
                  >
                    {{ timezone.cities.slice(0, 4).join(" · ") }}
                  </span>
                </div>
                <span class="timezone-selector__item-time">{{
                  getFormattedTime(timezone.value)
                }}</span>
              </div>
            </div>
          </template>
        </div>

        <div class="timezone-selector__footer" @click.stop="toggleDropdown">
          <Icon icon="mdi:earth" class="timezone-selector__footer-icon" />
          <span class="timezone-selector__footer-text">
            {{ selectedTimezoneLabel }} ({{ selectedTimezoneTime }})
          </span>
          <Icon icon="mdi:chevron-up" class="timezone-selector__footer-arrow" />
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import { Icon, Input } from "@/components/ui";
import { TIMEZONES, type TimezoneOption } from "@/utils/enums";
import { Teleport, computed, onMounted, onUnmounted, ref, watch } from "vue";

interface TimezoneGroup {
  region: string;
  timezones: TimezoneOption[];
}

type TimeFormat = "12h" | "24h";

interface Props {
  modelValue?: string;
  autoSetDefault?: boolean;
  label?: string;
  required?: boolean;
  id?: string;
  showFormatToggle?: boolean;
  format?: TimeFormat;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  autoSetDefault: false,
  required: false,
  showFormatToggle: false,
  format: "12h",
});

const emit = defineEmits<{
  "update:modelValue": [value: string];
  blur: [event: FocusEvent];
}>();

const searchQuery = ref("");
const use24Hour = ref(props.format === "24h");
const selectedTimezone = ref(props.modelValue || "");
const isOpen = ref(false);

// Watch for format prop changes when toggle is hidden
watch(
  () => props.format,
  (newFormat) => {
    if (!props.showFormatToggle) {
      use24Hour.value = newFormat === "24h";
    }
  },
  { immediate: true }
);

const timezoneSelectorId = computed(
  () => props.id || `timezone-selector-${Math.random().toString(36).substr(2, 9)}`
);
const dropdownStyle = ref<Record<string, string>>({});
const triggerRef = ref<HTMLElement | null>(null);
let timeUpdateInterval: ReturnType<typeof setInterval> | null = null;

// Group timezones by region
const timezoneGroups = computed((): TimezoneGroup[] => {
  const groups: Record<string, TimezoneOption[]> = {};
  const seenValues = new Set<string>();

  TIMEZONES.forEach((tz) => {
    let region = "OTHER";

    if (tz.value.startsWith("America/") || tz.value.startsWith("Pacific/Honolulu")) {
      if (
        tz.value.includes("New_York") ||
        tz.value.includes("Toronto") ||
        tz.value.includes("Chicago") ||
        tz.value.includes("Denver") ||
        tz.value.includes("Los_Angeles") ||
        tz.value.includes("Vancouver") ||
        tz.value.includes("Anchorage") ||
        tz.value.includes("Honolulu")
      ) {
        region = "US/CANADA";
      } else {
        region = "AMERICA";
      }
    } else if (tz.value.startsWith("Europe/")) {
      region = "EUROPE";
    } else if (tz.value.startsWith("Asia/")) {
      region = "ASIA";
    } else if (tz.value.startsWith("Australia/") || tz.value.startsWith("Pacific/")) {
      region = "AUSTRALIA/PACIFIC";
    } else if (tz.value.startsWith("Africa/")) {
      region = "AFRICA";
    }

    if (!groups[region]) {
      groups[region] = [];
    }

    // Skip duplicates with same IANA value
    if (seenValues.has(tz.value)) {
      return;
    }

    seenValues.add(tz.value);
    groups[region].push(tz);
  });

  // Sort regions and timezones within each region
  const sortedGroups: TimezoneGroup[] = [];
  const regionOrder = [
    "US/CANADA",
    "AMERICA",
    "EUROPE",
    "AFRICA",
    "ASIA",
    "AUSTRALIA/PACIFIC",
    "OTHER",
  ];

  regionOrder.forEach((region) => {
    if (groups[region]) {
      sortedGroups.push({
        region,
        timezones: groups[region],
      });
    }
  });

  return sortedGroups;
});

// Filter timezones based on search query
const filteredTimezoneGroups = computed((): TimezoneGroup[] => {
  if (!searchQuery.value.trim()) {
    return timezoneGroups.value;
  }

  const query = searchQuery.value.toLowerCase();
  const filtered: TimezoneGroup[] = [];

  timezoneGroups.value.forEach((group) => {
    const matchingTimezones = group.timezones.filter(
      (tz) =>
        tz.label.toLowerCase().includes(query) ||
        tz.value.toLowerCase().includes(query) ||
        group.region.toLowerCase().includes(query) ||
        (tz.cities ?? []).some((city) => city.toLowerCase().includes(query))
    );

    if (matchingTimezones.length > 0) {
      filtered.push({
        region: group.region,
        timezones: matchingTimezones,
      });
    }
  });

  return filtered;
});

const getFormattedTime = (timezone: string): string => {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: !use24Hour.value,
    });

    return formatter.format(now);
  } catch (error) {
    return "--:--";
  }
};

// Get selected timezone label
const selectedTimezoneLabel = computed(() => {
  const tz = TIMEZONES.find((tz) => tz.value === selectedTimezone.value);
  return tz?.label || "Select Timezone";
});

// Get selected timezone current time
const selectedTimezoneTime = computed(() => {
  if (!selectedTimezone.value) return "--:--";
  return getFormattedTime(selectedTimezone.value);
});

// Select timezone
const selectTimezone = (value: string) => {
  selectedTimezone.value = value;
  emit("update:modelValue", value);
  // Close dropdown after selection
  setTimeout(() => {
    isOpen.value = false;
  }, 100);
};

const updateDropdownPosition = () => {
  if (!triggerRef.value) return;

  const rect = triggerRef.value.getBoundingClientRect();
  const spacing = 8;
  const dropdownHeight = 380;

  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;

  // Determine if dropdown should appear above or below
  const shouldShowAbove = spaceBelow < dropdownHeight + spacing && spaceAbove > spaceBelow;

  dropdownStyle.value = {
    position: "fixed",
    top: shouldShowAbove
      ? `${rect.top - dropdownHeight - spacing}px`
      : `${rect.bottom + spacing}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    zIndex: "10070",
  };
};

// Toggle dropdown
const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    setTimeout(() => {
      updateDropdownPosition();
    }, 0);
  }
};

// Handle blur event
const handleBlur = (event: FocusEvent) => {
  const relatedTarget = event.relatedTarget as HTMLElement;
  if (
    relatedTarget &&
    !relatedTarget.closest(".timezone-selector-wrapper") &&
    !relatedTarget.closest(".timezone-selector")
  ) {
    setTimeout(() => {
      if (isOpen.value) {
        isOpen.value = false;
      }
      emit("blur", event);
    }, 200);
  } else if (!relatedTarget) {
    setTimeout(() => {
      emit("blur", event);
    }, 100);
  }
};

// Close dropdown when clicking outside
let clickOutsideHandler: ((event: MouseEvent) => void) | null = null;

// Update time every minute
const startTimeUpdate = () => {
  if (timeUpdateInterval) {
    clearInterval(timeUpdateInterval);
  }
  timeUpdateInterval = setInterval(() => {
    const temp = searchQuery.value;
    searchQuery.value = temp + " ";
    searchQuery.value = temp;
  }, 60000);
};

// Watch for prop changes
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue !== selectedTimezone.value) {
      selectedTimezone.value = newValue || "";
    }
  },
  { immediate: true }
);

// Emit change when selected timezone updates
watch(selectedTimezone, (newValue) => {
  if (newValue !== props.modelValue) {
    emit("update:modelValue", newValue);
  }
});

onMounted(() => {
  startTimeUpdate();

  // Set default timezone if needed
  if (!selectedTimezone.value && props.autoSetDefault) {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const tzExists = TIMEZONES.find((tz) => tz.value === userTimezone);
    if (tzExists) {
      selectedTimezone.value = userTimezone;
      emit("update:modelValue", userTimezone);
    }
  }

  // Handle clicks outside
  clickOutsideHandler = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (
      target &&
      !target.closest(".timezone-selector-wrapper") &&
      !target.closest(".timezone-selector")
    ) {
      isOpen.value = false;
    }
  };
  document.addEventListener("click", clickOutsideHandler);
  window.addEventListener("scroll", updateDropdownPosition, true);
  window.addEventListener("resize", updateDropdownPosition);
});

onUnmounted(() => {
  if (timeUpdateInterval) {
    clearInterval(timeUpdateInterval);
  }
  if (clickOutsideHandler) {
    document.removeEventListener("click", clickOutsideHandler);
  }
  window.removeEventListener("scroll", updateDropdownPosition, true);
  window.removeEventListener("resize", updateDropdownPosition);
});
</script>

<style scoped>
.timezone-selector-wrapper {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.timezone-selector__label {
  display: block;
  font-size: 12px;
  font-weight: 400;
  color: #344054;
  margin-bottom: 6px;
}

.timezone-selector__required {
  color: #dc3545;
  font-size: 1.1rem;
  margin-left: 2px;
}

.timezone-selector__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 12px;
  background-color: #ffffff;
  border: 1px solid #ced4da;
  border-radius: 7px;
  cursor: pointer;
  font-size: 0.85rem;
  min-height: 42px;
  transition: border-color 0.2s ease;
  gap: 8px;
}

.timezone-selector__trigger:hover {
  border-color: var(--color-border-hover);
}

.timezone-selector__trigger:focus,
.timezone-selector__trigger--open {
  outline: none !important;
  border-color: var(--color-primary) !important;
  box-shadow: var(--ring) !important;
}

.timezone-selector__trigger-globe {
  font-size: 1rem;
  color: #6b7280;
  flex-shrink: 0;
}

.timezone-selector__trigger-text {
  color: #282a30;
  flex: 1;
  text-align: left;
  font-size: 0.8rem;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.timezone-selector__trigger-time {
  font-size: 0.75rem;
  color: #6b7280;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
}

.timezone-selector__trigger-icon {
  font-size: 1rem;
  color: #999;
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.timezone-selector {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e4e4e7;
  box-shadow:
    0px 4px 6px -1px rgba(0, 0, 0, 0.1),
    0px 2px 4px -1px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: 380px;
}

.timezone-selector__search {
  padding: 8px 12px;
  border-bottom: 1px solid #e4e4e7;
}

.timezone-selector__search :deep(.ui-input__field) {
  height: 32px !important;
  font-size: 0.75rem !important;
}

.timezone-selector__search :deep(.ui-input__icon) {
  font-size: 1.1rem !important;
}

.timezone-selector__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e4e4e7;
}

.timezone-selector__header-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #282a30;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.timezone-selector__toggle {
  display: flex;
  align-items: center;
  gap: 8px;
}

.timezone-selector__toggle-option {
  font-size: 0.7rem;
  color: #9ca3af;
  transition: color 0.2s ease;
}

.timezone-selector__toggle-option--active {
  color: #ffffff;
}

.timezone-selector__toggle-switch {
  position: relative;
  width: 40px;
  height: 22px;
  background-color: #e5e7eb;
  border-radius: 11px;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
  padding: 0;
}

.timezone-selector__toggle-switch--active {
  background-color: #3e3efe;
}

.timezone-selector__toggle-slider {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  background-color: #ffffff;
  border-radius: 50%;
  transition: transform 0.2s ease;
}

.timezone-selector__toggle-switch--active .timezone-selector__toggle-slider {
  transform: translateX(18px);
}

.timezone-selector__list {
  max-height: 280px;
  overflow-y: auto;
  padding: 8px 0;
}

.timezone-selector__list::-webkit-scrollbar {
  width: 6px;
}

.timezone-selector__list::-webkit-scrollbar-track {
  background: transparent;
}

.timezone-selector__list::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.timezone-selector__list::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

.timezone-selector__group {
  margin-bottom: 8px;
}

.timezone-selector__group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.75rem;
  font-weight: 700;
  color: #282a30;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 8px 16px 4px;
}

.timezone-selector__group-count {
  font-size: 0.65rem;
  color: #9ca3af;
  font-weight: 400;
}

.timezone-selector__item {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 8px;
  padding: 10px 16px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  color: #282a30;
}

.timezone-selector__item:hover {
  background-color: #f9fafb;
}

.timezone-selector__item--selected {
  background-color: #3e3efe;
  color: #ffffff;
}

.timezone-selector__item--selected:hover {
  background-color: #3535e5;
}

.timezone-selector__item-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.timezone-selector__item-name {
  font-size: 0.8rem;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.timezone-selector__item-cities {
  font-size: 0.7rem;
  color: #9ca3af;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.timezone-selector__item--selected .timezone-selector__item-cities {
  color: rgba(255, 255, 255, 0.7);
}

.timezone-selector__item-time {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  background: #f3f4f6;
  border-radius: 6px;
  padding: 2px 6px;
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
}

.timezone-selector__item--selected .timezone-selector__item-time {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.timezone-selector__empty {
  padding: 24px 16px;
  text-align: center;
  font-size: 0.8rem;
  color: #9ca3af;
}

.timezone-selector__footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #e4e4e7;
  background-color: #fafafa;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.timezone-selector__footer:hover {
  background-color: #f3f4f6;
}

.timezone-selector__footer-icon {
  font-size: 1.1rem;
  color: #3e3efe;
}

.timezone-selector__footer-text {
  font-size: 0.8rem;
  font-weight: 500;
  color: #282a30;
  flex: 1;
}

.timezone-selector__footer-arrow {
  font-size: 1.1rem;
  color: #6b7280;
}
</style>
